import { AnimatePresence, animate, type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Children, createContext, type CSSProperties, isValidElement, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function CountUp({ value, delay = 0 }: { value: number; delay?: number }) {
  const [display, setDisplay] = useState(0);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (reducedMotion) { setDisplay(value); return; }
    const controls = animate(0, value, { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1], onUpdate: (latest) => setDisplay(Math.round(latest)) });
    return () => controls.stop();
  }, [value, delay, reducedMotion]);
  return <>{display}</>;
}

export function Button({ children, variant = 'primary', icon, ...props }: Omit<HTMLMotionProps<'button'>, 'children'> & { children: ReactNode; variant?: 'primary' | 'secondary' | 'danger' | 'ghost'; icon?: ReactNode }) {
  return <motion.button
    className={`button button-${variant}`}
    whileHover={props.disabled ? undefined : { y: -1 }}
    whileTap={props.disabled ? undefined : { scale: 0.975 }}
    transition={{ type: 'spring', stiffness: 520, damping: 32 }}
    {...props}
  >{icon}{children}</motion.button>;
}

export function IconButton({ label, children, ...props }: Omit<HTMLMotionProps<'button'>, 'children'> & { label: string; children: ReactNode }) {
  return <motion.button
    type="button"
    className="icon-button"
    aria-label={label}
    data-tooltip={label}
    whileHover={props.disabled ? undefined : { y: -1, scale: 1.025 }}
    whileTap={props.disabled ? undefined : { scale: 0.94 }}
    transition={{ type: 'spring', stiffness: 520, damping: 30 }}
    {...props}
  >{children}</motion.button>;
}

interface SelectOption { value: string; label: string; disabled: boolean }
type SelectRow = { kind: 'group'; label: string } | { kind: 'option'; index: number; option: SelectOption };

function nodeText(node: ReactNode): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
}

// Flatten <option>/<optgroup> children into a display list plus a parallel flat
// option array whose indexes match the rows' `index` — so keyboard navigation
// and value lookup share one coordinate space.
function parseSelectChildren(children: ReactNode): { options: SelectOption[]; rows: SelectRow[] } {
  const options: SelectOption[] = [];
  const rows: SelectRow[] = [];
  const addOption = (element: ReactElement) => {
    const props = element.props as { value?: string | number; children?: ReactNode; disabled?: boolean };
    const option: SelectOption = { value: String(props.value ?? ''), label: nodeText(props.children), disabled: Boolean(props.disabled) };
    rows.push({ kind: 'option', index: options.length, option });
    options.push(option);
  };
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'optgroup') {
      const props = child.props as { label?: string; children?: ReactNode };
      const before = options.length;
      rows.push({ kind: 'group', label: props.label ?? '' });
      Children.forEach(props.children, (option) => { if (isValidElement(option) && option.type === 'option') addOption(option); });
      if (options.length === before) rows.pop();
    } else if (child.type === 'option') {
      addOption(child);
    }
  });
  return { options, rows };
}

interface SelectProps {
  children: ReactNode;
  value?: string | number;
  onChange?: (event: { target: { value: string } }) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

// A custom accessible listbox that replaces the native <select> so the open
// option list can carry the app's theme (native popups are unstylable). Keeps
// the <option>/<optgroup> children API and a `{ target: { value } }` onChange so
// call sites read like a native select. Follows the WAI-ARIA select-only
// combobox pattern: focus stays on the trigger, the active option is tracked via
// aria-activedescendant, and the popup is portaled so modal overflow can't clip it.
export function Select({ children, className = '', value, onChange, disabled, required, id, name, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy }: SelectProps) {
  const { options, rows } = useMemo(() => parseSelectChildren(children), [children]);
  const reactId = useId();
  const listId = `${reactId}-listbox`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef({ text: '', at: 0 });
  const reducedMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const current = String(value ?? '');
  const selectedIndex = options.findIndex((option) => option.value === current);
  // When the controlled value matches no option, show nothing (consistent with
  // selectedIndex = -1 marking no row selected) rather than the first enabled
  // option's label, which would misrepresent an unmatched value as a real choice.
  const displayLabel = selectedIndex >= 0 ? options[selectedIndex].label : '';

  const firstEnabled = useCallback(() => options.findIndex((option) => !option.disabled), [options]);
  const stepActive = useCallback((from: number, dir: 1 | -1) => {
    if (options.length === 0) return -1;
    let index = from < 0 ? (dir === 1 ? -1 : 0) : from;
    for (let n = 0; n < options.length; n += 1) {
      index = (index + dir + options.length) % options.length;
      if (!options[index].disabled) return index;
    }
    return from;
  }, [options]);

  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const dropUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(140, Math.min(320, dropUp ? spaceAbove : spaceBelow));
    setMenuStyle({
      position: 'fixed', left: Math.round(rect.left), width: Math.round(rect.width), maxHeight,
      ...(dropUp ? { bottom: Math.round(window.innerHeight - rect.top + 4) } : { top: Math.round(rect.bottom + 4) }),
    });
  }, []);

  const openMenu = useCallback((startIndex?: number) => {
    if (disabled) return;
    const initial = startIndex ?? (selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : firstEnabled());
    setActive(initial);
    setOpen(true);
  }, [disabled, firstEnabled, options, selectedIndex]);

  const closeMenu = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  const choose = useCallback((index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    if (option.value !== current) onChange?.({ target: { value: option.value } });
    closeMenu();
  }, [options, current, onChange, closeMenu]);

  const runTypeahead = useCallback((char: string) => {
    const now = Date.now();
    const state = typeahead.current;
    state.text = now - state.at > 700 ? char : state.text + char;
    state.at = now;
    const query = state.text.toLowerCase();
    const start = active >= 0 ? active : 0;
    const order = state.text.length === 1
      ? Array.from({ length: options.length }, (_, k) => (start + 1 + k) % options.length)
      : options.map((_, k) => k);
    const match = order.find((k) => !options[k].disabled && options[k].label.toLowerCase().startsWith(query));
    if (match !== undefined) { if (open) setActive(match); else choose(match); }
  }, [active, options, open, choose]);

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const key = event.key;
    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') { event.preventDefault(); openMenu(); }
      else if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) runTypeahead(key);
      return;
    }
    switch (key) {
      case 'ArrowDown': event.preventDefault(); setActive((index) => stepActive(index, 1)); break;
      case 'ArrowUp': event.preventDefault(); setActive((index) => stepActive(index, -1)); break;
      case 'Home': event.preventDefault(); setActive(firstEnabled()); break;
      case 'End': event.preventDefault(); setActive(stepActive(0, -1)); break;
      case 'Enter': case ' ': event.preventDefault(); choose(active); break;
      case 'Tab': closeMenu(false); break;
      case 'Escape':
        // Swallow at the native layer so the Modal's document-level Escape
        // handler doesn't also close the dialog behind the open dropdown.
        event.preventDefault(); event.nativeEvent.stopImmediatePropagation(); closeMenu(); break;
      default: if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) runTypeahead(key);
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const reflow = () => place();
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    return () => { window.removeEventListener('scroll', reflow, true); window.removeEventListener('resize', reflow); };
  }, [open, place]);

  useEffect(() => {
    if (!open || active < 0) return;
    const menu = listRef.current;
    const option = menu?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (!menu || !option) return;
    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;
    const visibleTop = menu.scrollTop;
    const visibleBottom = visibleTop + menu.clientHeight;
    if (optionTop < visibleTop) menu.scrollTop = optionTop;
    else if (optionBottom > visibleBottom) menu.scrollTop = optionBottom - menu.clientHeight;
  }, [active, open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  return <span className={`select-control ${className}`.trim()}>
    <button ref={triggerRef} type="button" id={id} name={name} className="select-trigger" disabled={disabled}
      role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
      aria-activedescendant={open && active >= 0 ? `${reactId}-opt-${active}` : undefined}
      aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} aria-required={required || undefined}
      onClick={() => (open ? closeMenu() : openMenu())} onKeyDown={onTriggerKeyDown}>
      <span className="select-value">{displayLabel}</span>
      <ChevronDown className="select-chevron" size={16} aria-hidden="true" />
    </button>
    {createPortal(<AnimatePresence>{open && (
      <motion.ul ref={listRef} id={listId} role="listbox" className="select-menu" style={menuStyle}
        aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
        transition={{ duration: reducedMotion ? 0 : 0.15, ease: [0.22, 1, 0.36, 1] }}>
        {rows.map((row) => row.kind === 'group'
          ? <li key={`g-${row.label}`} role="presentation" className="select-group-label">{row.label}</li>
          : <li key={`${row.option.value}-${row.index}`} id={`${reactId}-opt-${row.index}`} role="option" data-index={row.index}
              aria-selected={row.index === selectedIndex} aria-disabled={row.option.disabled || undefined}
              className={`select-option${row.index === active ? ' active' : ''}${row.index === selectedIndex ? ' selected' : ''}${row.option.disabled ? ' disabled' : ''}`}
              onMouseEnter={() => { if (!row.option.disabled) setActive(row.index); }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(row.index)}>
              <span className="select-option-label">{row.option.label}</span>
              {row.index === selectedIndex && <Check size={14} className="select-option-check" aria-hidden="true" />}
            </li>)}
      </motion.ul>
    )}</AnimatePresence>, document.body)}
  </span>;
}

const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, children, onClose, wide = false, isDirty }: { open: boolean; title: string; children: ReactNode; onClose: () => void; wide?: boolean; isDirty?: () => boolean }) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  // Keep the latest dirty check and onClose in refs so requestClose stays
  // referentially stable. Parents pass an inline `onClose` arrow and hold the
  // form state, so they re-render on every keystroke; if requestClose changed
  // identity, the focus-trap effect below would resubscribe each keystroke and
  // its rAF would yank focus from the input back to the panel — making modal
  // forms untypeable.
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const requestClose = useCallback(() => {
    if (dirtyRef.current?.() && !window.confirm('放弃未保存的修改并关闭？')) return;
    closeRef.current();
  }, []);
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }));
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { requestClose(); return; }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!panelRef.current.contains(active)) { event.preventDefault(); first.focus(); return; }
      if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
      else if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [open, requestClose]);
  // Only treat a press that both starts and ends on the backdrop as a close, so
  // selecting text inside the dialog and releasing over the backdrop can't
  // discard a half-filled form.
  const backdropDown = useRef(false);
  const onBackdropMouseDown = (event: MouseEvent) => { backdropDown.current = event.target === event.currentTarget; };
  const onBackdropMouseUp = (event: MouseEvent) => {
    if (backdropDown.current && event.target === event.currentTarget) requestClose();
    backdropDown.current = false;
  };
  return <AnimatePresence>{open && (
    <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onBackdropMouseDown} onMouseUp={onBackdropMouseUp}>
      <motion.section ref={panelRef} tabIndex={-1} className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}
        initial={{ opacity: 0, y: 18, scale: 0.975 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.985 }}
        transition={{ type: 'spring', stiffness: 360, damping: 30, mass: 0.8 }}>
        <header className="modal-header"><h2 id={titleId}>{title}</h2><IconButton label="关闭" onClick={requestClose}><X size={18} /></IconButton></header>
        <div className="modal-body">{children}</div>
      </motion.section>
    </motion.div>
  )}</AnimatePresence>;
}

export function EmptyState({ icon, title, action }: { icon: ReactNode; title: string; action?: ReactNode }) {
  return <div className="empty-state"><motion.div className="empty-icon" initial={{ opacity: 0, scale: 0.82 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 380, damping: 24 }}>{icon}</motion.div><p>{title}</p>{action}</div>;
}

export function Status({ value }: { value: 'pending' | 'refreshing' | 'ready' | 'error' }) {
  const labels = { pending: '待生成', refreshing: '刷新中', ready: '正常', error: '异常' };
  return <span className={`status status-${value}`}><span className="status-dot" />{labels[value]}</span>;
}

export function Spinner({ label = '加载中' }: { label?: string }) {
  return <div className="loading" role="status"><span className="spinner" />{label}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span className="field-label">{label}</span>{children}{hint && <span className="field-hint">{hint}</span>}</label>;
}

// Edits a string[] as free text (newline- or comma-separated) while keeping the
// raw text as the source of truth. A naively controlled input that re-derives
// value from the parsed array on every keystroke eats the separator the moment
// it's typed (an empty trailing token is filtered away), making multi-value
// entry impossible by keyboard. Local text state avoids that; the parent
// re-seeds by remounting with a stable `key` when it needs an external reset.
export function TokenListInput({ value, onChange, separator, placeholder, ariaLabel, tokenMaxLength, required }: {
  value: string[];
  onChange: (values: string[]) => void;
  separator: 'newline' | 'comma';
  placeholder?: string;
  ariaLabel?: string;
  tokenMaxLength?: number;
  required?: boolean;
}) {
  const joiner = separator === 'newline' ? '\n' : ', ';
  const splitter = separator === 'newline' ? /\r?\n/ : /[,，]/;
  const [text, setText] = useState(() => value.join(joiner));
  const emit = (raw: string) => {
    setText(raw);
    onChange(raw.split(splitter).map((item) => item.trim()).filter(Boolean).map((item) => tokenMaxLength ? item.slice(0, tokenMaxLength) : item));
  };
  const shared = { value: text, placeholder, 'aria-label': ariaLabel, onChange: (event: { target: { value: string } }) => emit(event.target.value) };
  return separator === 'newline'
    ? <textarea {...shared} required={required} />
    : <input {...shared} required={required} />;
}

interface ToastValue { notify: (message: string, tone?: 'success' | 'error') => void }
const ToastContext = createContext<ToastValue>({ notify: () => undefined });
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error'; id: number } | null>(null);
  const notify = useCallback((message: string, tone: 'success' | 'error' = 'success') => {
    const id = Date.now();
    setToast({ message, tone, id });
    window.setTimeout(() => setToast((current) => current?.id === id ? null : current), 2_800);
  }, []);
  // Stable context value: without this, every toast (and its 2.8s auto-dismiss)
  // creates a new object identity and forces every useToast consumer to re-render
  // — which was silently reloading the file editor and discarding unsaved drafts.
  const value = useMemo(() => ({ notify }), [notify]);
  return <ToastContext.Provider value={value}>{children}<AnimatePresence>{toast && (
    <motion.div className={`toast toast-${toast.tone}`} initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.97 }} transition={{ type: 'spring', stiffness: 420, damping: 30 }}>
      {toast.tone === 'success' ? <Check size={17} /> : <X size={17} />}{toast.message}
    </motion.div>
  )}</AnimatePresence></ToastContext.Provider>;
}
